// components/members/invites.component
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { FaTimes } from "react-icons/fa";
import { db } from "../../firebase/firebase.utils";
import {
  respondToInvite,
  blockInviteSender,
  loadInviteLabels,
} from "../../firebase/memberFunctions";
import permissions from "../../utils/permissions";
import BoxLayer from "../retro/box-layer/box-layer.component";
import RetroButton from "../retro/button/retro-button.component";
import "./members.styles.scss";

const { ROLE_LABELS, getInviteRoleKey, getInviteDescription } = permissions;

// Mount once inside your signed-in Router layout. Opens for ?invites=1.
// You can alternatively control it with open/onClose and optional inviteId props.
export default function Invites({ open, onClose, inviteId: suppliedInviteId }) {
  const user = useSelector((state) => state.user.currentUser);
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const inviteId = suppliedInviteId || params.get("inviteId");
  const visible =
    open ??
    (location.pathname === "/invites" ||
      params.get("invites") === "1" ||
      Boolean(inviteId));
  const [invites, setInvites] = useState([]);
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const busyRef = useRef(false);
  const [pageSize, setPageSize] = useState(30);
  const [blockId, setBlockId] = useState(null);

  useEffect(() => {
    setInvites([]);
    setLabels({});
    setPageSize(30);
    setBlockId(null);
  }, [user?.uid, inviteId]);
  useEffect(() => {
    if (!visible || !user?.uid) return;
    setLoading(true);
    setError("");
    const ref = inviteId
      ? db.collection("invites").doc(inviteId)
      : db
          .collection("invites")
          .where("inviteeUid", "==", user.uid)
          .where("status", "in", ["pending", "accepted"])
          .orderBy("createdAt", "desc")
          .limit(pageSize);
    return ref.onSnapshot(
      (snapshot) => {
        const docs = inviteId
          ? snapshot.exists
            ? [snapshot]
            : []
          : snapshot.docs;
        setInvites(
          docs
            .filter((document) => document.data().inviteeUid === user.uid)
            .map((document) => ({ ...document.data(), id: document.id })),
        );
        setLoading(false);
      },
      (err) => {
        setInvites([]);
        setError(err.message);
        setLoading(false);
      },
    );
  }, [visible, user?.uid, inviteId, pageSize]);

  useEffect(() => {
    let active = true;
    if (!visible || !user?.uid || !invites.length) return;
    loadInviteLabels(invites)
      .then((data) => {
        if (active) setLabels(data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [invites, user?.uid, visible]);

  function close() {
    if (busyRef.current) return;
    if (onClose) return onClose();

    const next = new URLSearchParams(location.search);
    next.delete("invites");
    next.delete("inviteId");

    navigate(
      {
        pathname:
          location.pathname === "/invites" && !inviteId
            ? "/"
            : location.pathname,
        search: next.toString() ? `?${next}` : "",
        hash: location.hash,
      },
      { replace: true },
    );
  }
  useEffect(() => {
    if (!visible) return;
    const escape = (event) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", escape);
    return () => window.removeEventListener("keydown", escape);
  }, [visible, location.pathname, location.search, location.hash, onClose]);

  async function act(id, action) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusyId(id);
    setError("");
    try {
      await action();
      setBlockId(null);
    } catch (err) {
      setError(err.message || "Couldn't complete action.");
    } finally {
      busyRef.current = false;
      setBusyId(null);
    }
  }

  return (
    <AnimatePresence>
      {visible && user?.uid && (
        <BoxLayer setLayer={close}>
          <section
            className="members-invites"
            role="dialog"
            aria-modal="true"
            aria-labelledby="members-invites-title"
          >
            <header className="members-invites__header">
              <h3 id="members-invites-title">
                {inviteId ? "Invitation" : "My invitations"}
              </h3>
              <button
                type="button"
                disabled={Boolean(busyId)}
                onClick={close}
                aria-label="Close invitations"
              >
                <FaTimes />
              </button>
            </header>
            {!!error && (
              <p role="alert" className="members__error">
                {error}
              </p>
            )}
            {loading && <p role="status">Loading invitations…</p>}
            {!loading && !error && !invites.length && (
              <p>No invitations found.</p>
            )}
            {invites.map((invite) => {
              const target =
                invite.targetName ||
                labels[invite.id]?.targetName ||
                `${invite.targetType === "space" ? "Workspace" : "Project"} (${invite.stationId || invite.spaceId})`;
              const inviter =
                invite.inviterName || labels[invite.id]?.inviterName;
              return (
                <article className="members-invites__card" key={invite.id}>
                  <h4>{target}</h4>
                  <p>
                    {invite.targetType === "space" ? "Workspace" : "Project"}{" "}
                    invitation ·{" "}
                    <strong>{ROLE_LABELS[getInviteRoleKey(invite)]}</strong>
                  </p>
                  {!!inviter && <p>From {inviter}</p>}
                  <p className="members__help">
                    {getInviteDescription(invite)}
                  </p>
                  {invite.status === "accepted" && (
                    <p role="status">
                      {invite.membershipStatus === "pending"
                        ? "Accepted. Your access is being set up… You can close this window and check again later."
                        : invite.membershipStatus === "failed"
                          ? invite.membershipError ||
                            "Access could not be granted. Ask for a new invitation."
                          : invite.membershipStatus === "applied"
                            ? invite.grantResult === "already-member"
                              ? "Your existing access was kept."
                              : "You're in. Your access is ready."
                            : "This invitation was accepted previously. Check your project or workspace list for access."}
                    </p>
                  )}
                  {!["pending", "accepted"].includes(invite.status) && (
                    <p>This invitation is {invite.status}.</p>
                  )}
                  {invite.status === "pending" && (
                    <>
                      <div className="members-invites__actions">
                        <RetroButton
                          type="button"
                          disabled={Boolean(busyId)}
                          mode="gray"
                          onClick={() =>
                            act(invite.id, () =>
                              respondToInvite(invite.id, "declined"),
                            )
                          }
                        >
                          Decline
                        </RetroButton>
                        <RetroButton
                          type="button"
                          disabled={Boolean(busyId)}
                          onClick={() =>
                            act(invite.id, () =>
                              respondToInvite(invite.id, "accepted"),
                            )
                          }
                        >
                          {busyId === invite.id ? "Saving…" : "Accept"}
                        </RetroButton>
                        <RetroButton
                          type="button"
                          disabled={Boolean(busyId)}
                          mode="gray"
                          onClick={() => setBlockId(invite.id)}
                        >
                          Block inviter
                        </RetroButton>
                      </div>
                      {blockId === invite.id && (
                        <div>
                          <p>Block this person and decline this invitation?</p>
                          <div className="members-invites__actions">
                            <RetroButton
                              type="button"
                              mode="gray"
                              disabled={Boolean(busyId)}
                              onClick={() => setBlockId(null)}
                            >
                              Cancel
                            </RetroButton>
                            <RetroButton
                              type="button"
                              disabled={Boolean(busyId)}
                              onClick={() =>
                                act(invite.id, () =>
                                  blockInviteSender(invite.id),
                                )
                              }
                            >
                              Block
                            </RetroButton>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </article>
              );
            })}
            {!inviteId && invites.length === pageSize && (
              <RetroButton
                type="button"
                mode="gray"
                disabled={loading}
                onClick={() => setPageSize((value) => value + 30)}
              >
                Load more
              </RetroButton>
            )}
          </section>
        </BoxLayer>
      )}
    </AnimatePresence>
  );
}
